import { list, named, text, textarea, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { look, paint, typeface } from './styleTokens';

// A strip of names and, under it, one panel at a time. The children are the panels, in the order the
// names run; the names are a setting, so a merchant renames a tab without touching what is inside it.
//
// Sleek's home page leads with exactly this — "What's hot / Best sellers / Sale" over a grid of products,
// each tab a different collection — and the same element is its "Customers enjoy their journey" band
// with the names down the side. Nothing in a panel is special: a product grid, a picture, a paragraph,
// whatever a merchant drops there.
//
// In the builder every panel is drawn, stacked under its own name, because a panel that is hidden
// cannot be selected, styled or dropped into, and hunting for the third tab by clicking is not editing.

/** Where the names sit. Across the top is the common case; down the side is the reference's other use. */
const PLACEMENTS: readonly Choice[] = [['top', 'Across the top'], ['side', 'Down the side']];

/** The words a fresh strip lands with, one per starter panel. */
const STARTER_TABS = [{ label: "What's hot" }, { label: 'Best sellers' }, { label: 'Sale' }];

/** The tab strip: one child per tab, one tab shown at a time. */
export const tabsElement = element('tabs', {
  label: 'Tabs',
  description: 'A row of names with one panel shown at a time — each panel holds whatever you put in it.',
  category: 'Layouts',
  icon: 'panel-top',
  props: [
    { ...list('tabs', 'Tabs', [text('label', 'Name'), textarea('description', 'Sentence under the name')]),
      help: 'One name per panel, in order. Add a panel to add a tab. The sentence is only drawn when the names sit down the side.' },
    named('placement', 'Names sit', PLACEMENTS)
  ],
  defaults: {
    props: { tabs: STARTER_TABS, placement: 'top' },
    // No `display` here on purpose: the element sets its own from `placement`, and one pinned on the
    // node would outrank the stylesheet's side-by-side layout.
    styles: look({ width: '100%', fontFamily: typeface('body'), color: paint('heading') }),
    children: [{ type: 'container' }, { type: 'container' }, { type: 'container' }]
  },
});
